/* eslint-disable no-param-reassign */
import React from 'react';
import config from '../../../config';
import styles from './sub-menu.module.scss';
import { ReactComponent as ChevronIcon } from '../../../assets/svg/chevron.svg';
import closeIconUrl from '../../../assets/svg/close.svg';
import sanitizeId from '../../../utils/sanitizeId';
import { getSoleLink } from '../../../utils/linkSectionUtils';
import { SubMenuViewProps } from '../types';

const z3HrefForPath = (path: string) => {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${config.Z3_URL.replace(/\/$/, '')}${normalized}`;
};

const SubMenuView = ({
  activeMenu,
  activeSubMenu,
  children,
  firstSubMenuRef,
  handleKeyDown,
  menu,
  onCloseMenu,
  onReturnToSubMenu,
  onSingleSectionLinkClick,
  setActiveSubMenu,
  subMenuRefs,
}: SubMenuViewProps) => (
  <div
    aria-labelledby={`${sanitizeId(menu.name)}-menu`}
    aria-hidden={activeMenu !== menu.name}
    className={`${styles.submenu} ${activeMenu === menu.name ? styles.submenu__open : ''}`}
    id={`${sanitizeId(menu.name)}-panel`}
    role="group"
  >
    <button
      className={styles['submenu-button']}
      type="button"
      onClick={() => onCloseMenu(false)}
      aria-label={`Close ${menu.name} menu`}
      tabIndex={activeMenu === menu.name ? 0 : -1}
    >
      <ChevronIcon aria-hidden />
      {menu.name}
    </button>
    <ul aria-label={`${menu.name} sub-menu`}>
      {menu.linkSections.map((subMenu, index) => {
        const direct = getSoleLink(subMenu);
        if (direct) {
          return (
            <li
              className={styles['submenu-item']}
              id={`${sanitizeId(subMenu.name)}-sub-menu`}
              key={subMenu.name}
            >
              <a
                className={styles['submenu-item-button']}
                href={z3HrefForPath(direct.path)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => onSingleSectionLinkClick(e, subMenu)}
                onKeyDown={(e) => handleKeyDown(e, subMenu.name, index)}
                ref={(el) => {
                  subMenuRefs.current[subMenu.name] = el;
                  if (index === 0 && firstSubMenuRef) {
                    firstSubMenuRef.current[menu.name] = el;
                  }
                }}
                tabIndex={activeMenu === menu.name && activeSubMenu === subMenu.name && index === 0 ? 0 : -1}
              >
                {subMenu.name}
              </a>
            </li>
          );
        }
        return (
          <li
            className={styles['submenu-item']}
            id={`${sanitizeId(subMenu.name)}-sub-menu`}
            key={subMenu.name}
          >
            <button
              aria-controls={`${sanitizeId(subMenu.name)}-sub-menu-panel`}
              aria-describedby={activeSubMenu === subMenu.name ? `${sanitizeId(subMenu.name)}-instructions` : undefined}
              aria-expanded={activeSubMenu === subMenu.name}
              className={styles['submenu-item-button']}
              onClick={() => setActiveSubMenu(subMenu.name)}
              onKeyDown={(e) => handleKeyDown(e, subMenu.name, index)}
              ref={(el) => {
                subMenuRefs.current[subMenu.name] = el;
                if (index === 0 && firstSubMenuRef) {
                  firstSubMenuRef.current[menu.name] = el;
                }
              }}
              tabIndex={activeMenu === menu.name && activeSubMenu === subMenu.name && index === 0 ? 0 : -1}
              type="button"
            >
              {subMenu.name}
              {/* Hidden instructions for screen readers */}
              {activeSubMenu === subMenu.name && (
              <span
                id={`${sanitizeId(subMenu.name)}-instructions`}
                className="sr-only"
                aria-live="polite"
              >
                Press Arrow Right to open links, Arrow Left to close, Escape to close menu
              </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
    {/* Pass onReturnToSubMenu and isMenuActive to children (LinkList components) */}
    {React.Children.map(children, (child) => (React.isValidElement(child)
      ? React.cloneElement(child as React.ReactElement<Record<string, unknown>>, { onReturnToSubMenu, isMenuActive: activeMenu === menu.name })
      : child))}
    <div className={styles['submenu-close']}>
      <button
        aria-label="Close navigation menu"
        className={styles['submenu-close-button']}
        onClick={() => onCloseMenu()}
        tabIndex={activeMenu === menu.name ? 0 : -1}
        type="button"
      >
        Close
        <img src={closeIconUrl} alt="" aria-hidden />
      </button>
    </div>
  </div>
);

export default SubMenuView;
